/**
 * LinkTesterPage — Sandbox click through a tracking URL; inspect the full redirect chain.
 * User-agent spoofing, custom headers, optional cookies, latency trace.
 */
import { useState, type FormEvent } from "react";
import { useLinkTest } from "./hooks";
import type { HeaderPair, UserAgentPreset, TestLinkHop } from "./api";

// Inline icon primitives — keep dependency surface zero.
const iconProps = (size: number) => ({
	width: size,
	height: size,
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: 2,
	strokeLinecap: "round" as const,
	strokeLinejoin: "round" as const,
	"aria-hidden": true,
});
const IconPlus = ({ size = 14 }: { size?: number }) => (
	<svg {...iconProps(size)}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
);
const IconTrash = ({ size = 14 }: { size?: number }) => (
	<svg {...iconProps(size)}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
);
const IconArrowRight = ({ size = 12 }: { size?: number }) => (
	<svg {...iconProps(size)}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
);
const IconCheck = ({ size = 12 }: { size?: number }) => (
	<svg {...iconProps(size)}><circle cx="12" cy="12" r="10" /><polyline points="8 12 11 15 16 9" /></svg>
);
const IconAlert = ({ size = 12 }: { size?: number }) => (
	<svg {...iconProps(size)}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
);
const IconClock = ({ size = 12 }: { size?: number }) => (
	<svg {...iconProps(size)}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
);

const UA_OPTIONS: { value: UserAgentPreset; label: string }[] = [
	{ value: "chrome", label: "Chrome 120 (Desktop)" },
	{ value: "safari", label: "Safari 17 (macOS)" },
	{ value: "firefox", label: "Firefox 121 (Desktop)" },
	{ value: "mobile", label: "Mobile Safari (iPhone)" },
	{ value: "bot", label: "Googlebot" },
];

export function LinkTesterPage() {
	const testMut = useLinkTest();
	const [url, setUrl] = useState("");
	const [userAgent, setUserAgent] = useState<UserAgentPreset>("chrome");
	const [headers, setHeaders] = useState<HeaderPair[]>([]);
	const [cookies, setCookies] = useState("");

	const addHeaderRow = () => {
		setHeaders((h) => [...h, { key: "", value: "" }]);
	};
	const updateHeader = (idx: number, field: "key" | "value", value: string) => {
		setHeaders((h) => h.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
	};
	const removeHeader = (idx: number) => {
		setHeaders((h) => h.filter((_, i) => i !== idx));
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (!url.trim()) return;
		const cleanHeaders = headers.filter((h) => h.key.trim() && h.value.trim());
		testMut.mutate({
			url: url.trim(),
			userAgent,
			headers: cleanHeaders.length > 0 ? cleanHeaders : undefined,
			cookies: cookies.trim() || undefined,
		});
	};

	const result = testMut.data;

	return (
		<main
			data-testid="link-tester-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-6"
		>
			<header className="flex flex-col gap-1">
				<h1 className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-2xl font-semibold text-transparent">
					Link Tester
				</h1>
				<p className="text-sm text-zinc-500">
					Sandbox click through a tracking URL. Inspect every redirect hop, header, and set-cookie along the chain.
				</p>
			</header>

			<form
				onSubmit={handleSubmit}
				className="flex flex-col gap-5 rounded-lg border border-zinc-200 bg-white p-6 shadow-sm"
				data-testid="link-tester-form"
				aria-label="link-tester form"
			>
				<div className="flex flex-col gap-1">
					<label htmlFor="link-tester-url" className="block text-sm font-medium text-zinc-700 mb-1">
						Tracking URL
					</label>
					<input
						id="link-tester-url"
						type="url"
						required
						placeholder="https://track.example.com/click?c=123"
						className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none placeholder:text-zinc-400"
						value={url}
						onChange={(e) => setUrl(e.currentTarget.value)}
						data-testid="link-tester-url-input"
					/>
					<p className="text-xs text-zinc-500 mt-1">Full URL including protocol and query string.</p>
				</div>

				<div className="flex flex-col gap-1">
					<label htmlFor="link-tester-ua" className="block text-sm font-medium text-zinc-700 mb-1">
						User-Agent
					</label>
					<select
						id="link-tester-ua"
						className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none"
						value={userAgent}
						onChange={(e) => setUserAgent(e.currentTarget.value as UserAgentPreset)}
						data-testid="link-tester-ua-select"
					>
						{UA_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>

				<div className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<label className="block text-sm font-medium text-zinc-700">Custom Headers</label>
						<button
							type="button"
							onClick={addHeaderRow}
							className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
							data-testid="link-tester-add-header"
						>
							<IconPlus size={14} /> Add row
						</button>
					</div>
					{headers.length === 0 ? (
						<p className="text-xs text-zinc-500">No custom headers. Click Add row to include one.</p>
					) : (
						<div className="flex flex-col gap-2" data-testid="link-tester-header-rows">
							{headers.map((row, idx) => (
								<div key={idx} className="flex items-center gap-2">
									<input
										type="text"
										placeholder="Header name"
										className="flex-1 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none"
										value={row.key}
										onChange={(e) => updateHeader(idx, "key", e.currentTarget.value)}
										data-testid={`link-tester-header-key-${idx}`}
									/>
									<input
										type="text"
										placeholder="Value"
										className="flex-1 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none"
										value={row.value}
										onChange={(e) => updateHeader(idx, "value", e.currentTarget.value)}
										data-testid={`link-tester-header-val-${idx}`}
									/>
									<button
										type="button"
										onClick={() => removeHeader(idx)}
										className="inline-flex items-center rounded-md border border-red-200 bg-white px-2 py-2 text-red-700 hover:bg-red-50"
										aria-label={`Remove header ${idx + 1}`}
										data-testid={`link-tester-header-rm-${idx}`}
									>
										<IconTrash size={14} />
									</button>
								</div>
							))}
						</div>
					)}
				</div>

				<div className="flex flex-col gap-1">
					<label htmlFor="link-tester-cookies" className="block text-sm font-medium text-zinc-700 mb-1">
						Cookies (optional)
					</label>
					<textarea
						id="link-tester-cookies"
						rows={3}
						placeholder="name1=value1; name2=value2"
						className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none placeholder:text-zinc-400"
						value={cookies}
						onChange={(e) => setCookies(e.currentTarget.value)}
						data-testid="link-tester-cookies-input"
					/>
					<p className="text-xs text-zinc-500 mt-1">Passed as a single Cookie header on the initial request.</p>
				</div>

				<div className="flex justify-end gap-3 mt-2">
					<button
						type="submit"
						className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
						disabled={testMut.isPending || !url.trim()}
						data-testid="link-tester-submit"
					>
						{testMut.isPending ? "Tracing." : "Run trace"}
					</button>
				</div>
			</form>

			{testMut.error ? (
				<div
					className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
					role="alert"
					data-testid="link-tester-error"
				>
					<strong>Trace failed:</strong> {testMut.error.message}
				</div>
			) : null}

			{result ? (
				<section
					className="flex flex-col gap-3"
					data-testid="link-tester-result"
					aria-label="link-tester result"
				>
					<div className="flex items-center justify-between">
						<h2 className="text-xl font-semibold text-zinc-900">Redirect chain</h2>
						<div className="flex items-center gap-3 text-sm">
							<span
								className={
									"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium " +
									(result.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")
								}
							>
								{result.ok ? <IconCheck size={12} /> : <IconAlert size={12} />}
								{result.ok ? "OK" : "Failed"}
							</span>
							<span className="text-zinc-500">
								{result.hop_count} hop{result.hop_count === 1 ? "" : "s"}
							</span>
						</div>
					</div>

					<ol className="flex flex-col gap-3" data-testid="link-tester-hop-list">
						{result.hops.map((hop) => (
							<HopCard key={hop.step} hop={hop} />
						))}
					</ol>

					<div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm">
						<div className="flex items-center gap-2 text-zinc-700">
							<span className="text-zinc-500"><IconClock size={14} /></span>
							<span className="font-medium">Total latency:</span>
							<span data-testid="link-tester-total-latency">{result.total_latency_ms}ms</span>
						</div>
						<div className="flex items-center gap-2 text-zinc-700">
							<span className="text-xs uppercase tracking-wider text-zinc-500">Final</span>
							<span className="text-zinc-400"><IconArrowRight size={12} /></span>
							<span className="font-mono text-xs text-zinc-900">{result.final_url}</span>
						</div>
					</div>
				</section>
			) : null}
		</main>
	);
}

function HopCard({ hop }: { hop: TestLinkHop }) {
	const isError = hop.error != null || hop.status >= 400;
	const isRedirect = hop.status >= 300 && hop.status < 400;
	const borderClass = isError
		? "border-l-4 border-l-red-200"
		: "border-l-4 border-l-emerald-500";
	const statusBadge = isError
		? "bg-red-50 text-red-700"
		: isRedirect
			? "bg-sky-50 text-sky-700"
			: "bg-emerald-50 text-emerald-700";
	const headerEntries = Object.entries(hop.response_headers);

	return (
		<li
			className={`flex flex-col gap-2 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm ${borderClass}`}
			data-testid={`link-tester-hop-${hop.step}`}
		>
			<div className="flex items-center gap-3">
				<span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
					Hop {hop.step}
				</span>
				<span className="rounded-md bg-zinc-100 px-2 py-0.5 font-mono text-xs text-zinc-700">
					{hop.method}
				</span>
				<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge}`}>
					{hop.status} {hop.statusText}
				</span>
				<span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-500">
					<IconClock size={12} />
					{hop.latency_ms}ms
				</span>
			</div>
			<div className="break-all font-mono text-xs text-zinc-900">{hop.url}</div>

			{hop.error ? (
				<div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
					{hop.error}
				</div>
			) : null}

			{hop.set_cookies.length > 0 ? (
				<details className="text-xs text-zinc-700" data-testid={`link-tester-hop-${hop.step}-cookies`}>
					<summary className="cursor-pointer text-zinc-500 hover:text-zinc-700">
						Set-Cookie ({hop.set_cookies.length})
					</summary>
					<ul className="mt-2 flex flex-col gap-1 font-mono text-xs">
						{hop.set_cookies.map((c, i) => (
							<li key={i} className="break-all rounded bg-zinc-50 px-2 py-1 text-zinc-700">
								{c}
							</li>
						))}
					</ul>
				</details>
			) : null}

			{headerEntries.length > 0 ? (
				<details className="text-xs text-zinc-700" data-testid={`link-tester-hop-${hop.step}-headers`}>
					<summary className="cursor-pointer text-zinc-500 hover:text-zinc-700">
						Response headers ({headerEntries.length})
					</summary>
					<table className="mt-2 w-full border-collapse">
						<tbody>
							{headerEntries.map(([k, v]) => (
								<tr key={k} className="border-t border-zinc-200">
									<td className="px-2 py-1 font-mono text-xs text-zinc-500">{k}</td>
									<td className="px-2 py-1 font-mono text-xs text-zinc-900 break-all">{v}</td>
								</tr>
							))}
						</tbody>
					</table>
				</details>
			) : null}
		</li>
	);
}

export default LinkTesterPage;
